import React from 'react';
import { CheckIcon, HourglassIcon, MinusIcon, RotateCcwIcon, ShieldCheckIcon, SendIcon } from 'lucide-react';
import { TaskTemplate } from '../../types/family';
import { OccurrenceStatus } from '../../types/activity';
import { useAppState } from '../../contexts/AppStateContext';
import { useUi } from '../../contexts/UiContext';
interface TaskCheckboxProps {
  template: TaskTemplate;
  status: OccurrenceStatus;
  excused?: boolean;
}
export function TaskCheckbox({
  template,
  status,
  excused
}: TaskCheckboxProps) {
  const {
    checkTrust,
    uncheck,
    submitForReview
  } = useAppState();
  const {
    openTask
  } = useUi();
  const trust = template.confirmation === 'zaupanje';
  if (excused) {
    return <span className="grid h-11 w-11 shrink-0 place-items-center" aria-label={`${template.title}: oproščeno`} data-id="element-1000" data-mp="2x3smmrsumt9m">
        <span className="grid h-7 w-7 place-items-center rounded-lg border-2 border-excused bg-excused-soft text-excused-ink" data-id="element-1001" data-mp="2j63z1c7ed80v">
          <MinusIcon className="h-4 w-4" strokeWidth={3} aria-hidden="true" data-id="element-1002" data-mp="2o348zy7daux9" />
        </span>
      </span>;
  }
  const handle = () => {
    if (trust) {
      if (status === 'opravljeno') uncheck(template.id);else checkTrust(template.id);
      return;
    }
    if (status === 'odprto' || status === 'popravek') submitForReview(template.id);else openTask(template.id);
  };
  let label: string;
  let visual: React.ReactNode;
  if (trust) {
    const done = status === 'opravljeno';
    label = done ? `${template.title}: označi kot neopravljeno` : `${template.title}: označi kot opravljeno`;
    visual = <span className={`grid h-7 w-7 place-items-center rounded-lg border-2 transition-[background-color,border-color,transform] duration-150 ease-out ${done ? 'scale-105 border-done bg-done text-white' : 'border-[#C9BBA4] bg-surface text-transparent group-hover:border-done'}`} data-id="element-1003" data-mp="13yoys8yqwk3g">
        <CheckIcon className="h-4 w-4" strokeWidth={3.5} aria-hidden="true" data-id="element-1004" data-mp="6junqa7r0462" />
      </span>;
  } else {
    const map: Record<OccurrenceStatus, {
      cls: string;
      icon: React.ReactNode;
      text: string;
    }> = {
      odprto: {
        cls: 'border-[#C9BBA4] bg-surface text-muted group-hover:border-accent group-hover:text-accent',
        icon: <SendIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1005" data-mp="2gbvu8xmcf9w4" />,
        text: 'oddaj v pregled'
      },
      popravek: {
        cls: 'border-accent bg-accent-soft text-accent-ink',
        icon: <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1006" data-mp="1nnzzhygc3s1z" />,
        text: 'ponovno oddaj v pregled'
      },
      v_pregledu: {
        cls: 'border-review bg-review-soft text-review-ink',
        icon: <HourglassIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1007" data-mp="1xk2xcs45l4j4" />,
        text: 'v pregledu, odpri podrobnosti'
      },
      potrjeno: {
        cls: 'border-done bg-done text-white',
        icon: <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1008" data-mp="38z9jsanpp367" />,
        text: 'potrjeno, odpri podrobnosti'
      },
      opravljeno: {
        cls: 'border-done bg-done text-white',
        icon: <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1009" data-mp="11czonr4s5l6r" />,
        text: 'opravljeno'
      }
    };
    const m = map[status];
    label = `${template.title}: ${m.text}`;
    visual = <span className={`grid h-7 w-7 place-items-center rounded-lg border-2 transition-colors duration-150 ${m.cls}`} data-id="element-1010" data-mp="sl65dwxv9wdh">{m.icon}</span>;
  }
  return <button type="button" onClick={handle} aria-label={label} aria-pressed={trust ? status === 'opravljeno' : undefined} className="group grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-transform duration-100 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent active:scale-95" data-id="element-1011" data-mp="nks7h7pzc2t6">
      {visual}
    </button>;
}